import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Save, AlertTriangle, Copy, Send, PlusCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'osc_admin_token';

const PKG_LIST = [
  { id: 'diamond',  name: 'DIAMOND AMC PLAN',        price: 4999 },
  { id: 'basic',    name: 'BASIC SERVICE PACKAGE',   price: 3999 },
  { id: 'premium',  name: 'PREMIUM SERVICE PACKAGE', price: 5999 },
  { id: 'luxury',   name: 'LUXURY CAR CARE',         price: 9999 },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

export function EditBookingDialog({ booking, open, onOpenChange, onSaved }) {
  const [name, setName] = useState(booking?.name || '');
  const [mobile, setMobile] = useState(booking?.mobile || '');
  const [carModel, setCarModel] = useState(booking?.car_model || '');
  const [pkgId, setPkgId] = useState(booking?.package_id || 'diamond');
  const [date, setDate] = useState((booking?.booking_date || '').slice(0, 10));
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (booking) {
      setName(booking.name || ''); setMobile(booking.mobile || '');
      setCarModel(booking.car_model || ''); setPkgId(booking.package_id || 'diamond');
      setDate((booking.booking_date || '').slice(0, 10));
    }
  }, [booking]);

  const save = async () => {
    if (!booking) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/bookings/${booking.id}`, {
        name, mobile, car_model: carModel, package_id: pkgId,
        booking_date: new Date(date).toISOString(),
      }, { headers: authHeaders() });
      toast({ title: 'Booking updated' });
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f16] border-purple-900/60 text-gray-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="gradient-text text-xl">Edit Booking</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">ID: {booking?.id}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-300 text-xs">Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Mobile</Label>
            <Input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="text-gray-300 text-xs">Car Model</Label>
            <Input value={carModel} onChange={e => setCarModel(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Package</Label>
            <Select value={pkgId} onValueChange={setPkgId}>
              <SelectTrigger className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14141c] border-purple-900/60 text-white">
                {PKG_LIST.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Booking Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-purple-900/60 text-gray-200">Cancel</Button>
          <Button onClick={save} disabled={saving} className="gradient-btn rounded-full h-10 px-5 font-semibold"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteBookingDialog({ booking, open, onOpenChange, onDeleted }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast({ title: 'Reason required (min 3 chars)' }); return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/bookings/${booking.id}`, {
        headers: authHeaders(), params: { reason: reason.trim() },
      });
      toast({ title: 'Booking deleted', description: 'Archived for audit' });
      setReason('');
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f16] border-red-900/60 text-gray-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-400 text-xl flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Delete Booking</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">ID: {booking?.id} · {booking?.name} · {booking?.package_name}</DialogDescription>
        </DialogHeader>
        <Label className="text-gray-300 text-xs">Reason (required) *</Label>
        <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Customer request, duplicate booking, wrong details..." rows={3} className="bg-[#14141c] border-red-900/60 text-white text-sm mt-1" />
        <p className="text-[11px] text-yellow-400/80 mt-1">Deletion is logged with reason and archived for audit.</p>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-purple-900/60 text-gray-200">Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white rounded-full h-10 px-5 font-semibold">{busy ? 'Deleting...' : 'Delete'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ManualBookingDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [carModel, setCarModel] = useState('');
  const [pkgId, setPkgId] = useState('diamond');
  const [date, setDate] = useState('');
  const [payment, setPayment] = useState('token');
  const [sendLink, setSendLink] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setName(''); setMobile(''); setCarModel(''); setPkgId('diamond'); setDate(''); setPayment('token'); setSendLink(true); setResult(null); };

  const submit = async () => {
    if (!name || !mobile || !carModel || !date) { toast({ title: 'All fields required' }); return; }
    if (!/^\d{10}$/.test(mobile)) { toast({ title: 'Mobile must be 10 digits' }); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/manual`, {
        name, mobile, car_model: carModel, package_id: pkgId,
        booking_date: new Date(date).toISOString(),
        payment_type: payment, send_link: sendLink,
      }, { headers: authHeaders() });
      setResult(data);
      toast({ title: 'Manual booking created', description: `ID: ${data.id}` });
      onCreated?.();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!result?.payment_link) return;
    navigator.clipboard.writeText(result.payment_link);
    toast({ title: 'Payment link copied' });
  };

  const waShare = () => {
    if (!result?.payment_link) return;
    const msg = `નમસ્તે ${result.name},\n\nOne Stop Carz - ${result.package_name}\nસર્વિસ તારીખ: ${result.booking_date.slice(0,10)}\nચુકવવાની રકમ: ₹${result.amount}\n\nપેમેન્ટ કરો: ${result.payment_link}\n\n— One Stop Carz`;
    window.open(`https://wa.me/91${result.mobile}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="bg-[#0f0f16] border-purple-900/60 text-gray-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="gradient-text text-xl flex items-center gap-2"><PlusCircle className="w-5 h-5 text-pink-400" /> Manual Booking</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">Create booking on customer's behalf. Payment link auto-generated.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300 text-xs">Name *</Label><Input value={name} onChange={e => setName(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" /></div>
              <div><Label className="text-gray-300 text-xs">Mobile *</Label><Input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" /></div>
              <div className="col-span-2"><Label className="text-gray-300 text-xs">Car Model *</Label><Input value={carModel} onChange={e => setCarModel(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" /></div>
              <div>
                <Label className="text-gray-300 text-xs">Package *</Label>
                <Select value={pkgId} onValueChange={setPkgId}>
                  <SelectTrigger className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14141c] border-purple-900/60 text-white">{PKG_LIST.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-gray-300 text-xs">Service Date *</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1" /></div>
              <div className="col-span-2">
                <Label className="text-gray-300 text-xs">Payment Type *</Label>
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger className="bg-[#14141c] border-purple-900/60 text-white h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14141c] border-purple-900/60 text-white">
                    <SelectItem value="token">Token (₹100)</SelectItem>
                    {pkgId === 'diamond' && <SelectItem value="partial">Partial 25%</SelectItem>}
                    <SelectItem value="full">Full Payment (Incl. 18% GST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="col-span-2 flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input type="checkbox" checked={sendLink} onChange={e => setSendLink(e.target.checked)} className="accent-pink-500" />
                Auto-send payment link on WhatsApp (needs 24h session)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-purple-900/60 text-gray-200">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="gradient-btn rounded-full h-10 px-5 font-semibold">{busy ? 'Creating...' : 'Create & Get Payment Link'}</Button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-green-500/10 border border-green-500/40 p-4">
              <div className="text-green-400 font-semibold text-sm">✅ Booking Created</div>
              <div className="text-white font-mono text-xs mt-1">{result.id}</div>
              <div className="text-xs text-gray-300 mt-2">{result.name} · {result.mobile} · ₹{result.amount}</div>
            </div>
            {result.payment_link ? (
              <>
                <Label className="text-gray-300 text-xs">Payment Link</Label>
                <div className="flex gap-2">
                  <Input value={result.payment_link} readOnly className="bg-[#14141c] border-purple-900/60 text-white text-xs h-9 font-mono" />
                  <Button onClick={copyLink} variant="outline" className="border-purple-900/60 text-gray-200 shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
                <Button onClick={waShare} className="gradient-btn rounded-full h-11 w-full font-semibold">
                  <Send className="w-4 h-4 mr-2" /> Share on WhatsApp
                </Button>
                <p className="text-[11px] text-gray-400">Customer pays → booking auto-confirms → PDF invoice/receipt issued.</p>
              </>
            ) : (
              <p className="text-red-400 text-sm">Payment link generation failed. Contact support.</p>
            )}
            <Button onClick={() => { reset(); onOpenChange(false); }} variant="outline" className="w-full border-purple-900/60 text-gray-200">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
